import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef, Component,
  EventEmitter, Injector, Input,
  OnChanges, OnInit,
  Output, SimpleChanges, ViewChild, ViewContainerRef
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  BasicWidgetInfo, BasicWidgetInfoAotMode, BUILD_VERSION_PLACEHOLDER, DataWithDims, Dimension, DpValue, exportExcel, FilterSchema, FILTERS_WIDGET_NAME, FILTERS_WIDGET_PROP_NAME,
  FilterWidgetEventParam, WEB_PATH_HOME, LinkConfig,
  loadCommonStyleNTheme, mergeDpValues, mergeDpValuesForNew, RuntimeAggOption, WidgetAnonymityRepo, WidgetContainerMode, WidgetInfo, WidgetRepo, RtfUtilService, Log,
  DpFsVal,
  curLang
} from '@gspwidget/util';
import {
  FilterConfig,
  FILTER_PROP_NAME,
  RegisteredWidgetConfig, WidgetBase, WidgetDataService,
  WidgetEventService, WidgetInstanceInfo, WidgetOnRefresh
} from '@gspwidget/widget-devkit';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import ResizeObserver from 'resize-observer-polyfill';
import { Observable, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { DynamicLoaderService, WidgetLoadedData } from '../dynamic-loader.service';
import { InstanceService } from '../instance.service';
import { TitleBarComponent } from '../title-bar/title-bar.component';
import { COMMON_STYLE_PROP_NAME, jsBeforeLoadProp, jsOnBootProp, SHOW_RUNTIME_AGG_PROP_NAME } from '../widget-property.service';
import { chartAggFieldMap } from './runtime-agg';

/** 已被 set 翻译的 lang，下次不再请求此 lang */
// private langsSet: { [widgetName: string]: string[] } = {}
const loadedI18nUrls: string[] = []

declare global {
  interface Window {
    widgetI18nResLoaded: boolean
    widgetStyleLoaded: boolean
    define: any
  }
}
export enum WCStatus {
  LoadingWidget,
  LoadingData,
  DataEmpty,
  Normal,
  DataError,
  WidgetLoadError,
}
export interface GetDataOptions {
  dpValues: DpValue[],
  runtimeAgg: RuntimeAggOption
}
export interface FeFilterConfig {
  fieldName: string
  value: any
  // TODO: 暂时默认判断是否相等
  // operation: 
}

loadCommonStyleNTheme()
/**
 * 元部件信息传入方式可选以下任意一种：
 * 1. widgetId
 * 2. basicWidgetInfo
 *
 * 兼容阶段数据加载逻辑
 *    若元部件 useDataEngine === true, 说明是老写法、且需要数据的元部件，但其内部未调用加载数据，所以初始化完成后加载一下；
 *    新数据加载请求处理永远启用，因为仅有新写法元部件会触发；
 */
@Component({
  selector: 'lib-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss']
})
export class WidgetComponent implements OnInit, OnChanges {
  /** 元部件 id，widgetId 与 basicWidgetInfo 任选其一 */
  @Input() widgetId: string
  /** widget 数据库信息，widgetId 与 basicWidgetInfo 任选其一 */
  @Input() basicWidgetInfo: BasicWidgetInfo

  /** 必选，此 widget 的配置信息；仅在元部件加载时起作用，若属性改变，必需调用 setPropValues() */
  @Input() propValues
  /**
   * 处理前的 propValues
   * 注意区分：
   *  1. 处理前：ce、存储 用的都是处理前，包含完整数据
   *  2. 处理后：元部件内部 用的是处理后，仅包含必要信息
   * 
   * TODO: Input() 应该是谁？是否要经过处理？
   */
  propValuesBeforeProcess
  /**
   * 可选。最终用于给加载器，决定用哪种方式获取组件 factory
   * 传 widget id 的话，不用传此选项。
   * 传 basicWidgetInfo 的话，需要传此选项。
   * TODO: 为什么不直接加到 basicWidgetInfo 里？
   * */
  @Input() mode = WidgetContainerMode.AOT
  @Input() isDevMode = false
  /** 可选，widget 的静态资源地址，一般不用管，特殊情况可配置，默认值为 this.basicWidgetInfo.baseUrl + '/assets/' */
  @Input() assetsBaseUrl: string
  /** 可选，部件实例 id, 获取数据可能用到 */
  @Input() instanceId: string
  /**
   * 必选，元部件内 dataService.loadData() 等主动获取数据时，会调用此方法。
   * 
   * 返回 observable
   *    有值 - 正常展示数据
   *    空值、空数组等
   *      showIfNoData 为真 - 正常显示数据
   *      showIfNoData 为假 - 显示“暂无数据”
   * 返回空 - 无动作
   * 报错
   *    showIfNoData 为真 - 正常显示数据
   *    showIfNoData 为假 - 显示“数据加载失败”
   */
  @Input() getData: (getDataOpts: GetDataOptions) => Observable<DataWithDims>
  /**
   * 部件标题 fallback：
   * 若 propValues 中存在 title 的值，优先用它做部件标题；若没有，才用此作为 fallback
   * */
  @Input() fallbackTitle: string
  @Input() linkMap: LinkConfig
  // TODO: 改为 behaviorSubject
  @Input() status = WCStatus.LoadingWidget
  // TODO: portlet 相关都放到 mixin
  /** 会被合并、覆盖到 widgetInstanceInfo, 被元部件拿到. 可传入 portletId. */
  @Input() widgetInstanceInfoMixin: WidgetInstanceInfo = {}

  /** 三个优先级不同的 wcs，中优先级 wcs 来自元部件公共配置 */
  @Input() wcsHigher = null
  @Input() wcsLower = null
  /**
   * 传入初始化的 dp val list，在元部件第一次取数时，就会带上
   * 当前用于页面 url 传的 page filter value 的接收过程
   */
  @Input() initFilterDpFsValList: DpFsVal[]

  /** 当前这个 comp 实例化完成，用于外部得到此组件实例 */
  @Output() widgetCompCreated = new EventEmitter<WidgetComponent>()
  // TODO: 这几个事件，整理名字、时机
  @Output() widgetInfoLoaded = new EventEmitter<WidgetInfo>()
  /** widget 加载完成，发出当前 widget 的信息 */
  @Output() widgetLoaded = new EventEmitter<WidgetLoadedData>()
  /** 元部件加载、初始化完成，发出当前 widget 的信息 */
  @Output() widgetInit = new EventEmitter()
  @Output() runtimeAggChange = new EventEmitter()

  @ViewChild('resizeTarget') resizeTarget
  @ViewChild('wc', { read: ViewContainerRef }) widgetContainer: ViewContainerRef
  @ViewChild('titleBar') titleBar: TitleBarComponent

  // TODO: 封装成类似 subject, eventEmitter 似的用法
  private resolveWidgetLoadedPromise: (value?: unknown) => void
  public widgetLoadedPromise = new Promise((resolve, reject) => {
    this.resolveWidgetLoadedPromise = resolve
  })
  // public isLoading = true
  // public status = WCStatus.Normal
  public WCStatus = WCStatus
  public IGIX_HOME_PATH = WEB_PATH_HOME
  public FILTER_PROP_NAME = FILTER_PROP_NAME
  public width: number
  public height: number
  public curWidgetConfig: RegisteredWidgetConfig
  // public feFilterValues: FrontendFilter[]
  /** 存在此层的数据。已用于导出 excel。 */
  public data: any[] = null
  /** 获取数据后存起来的维度。可用于页面设计时、运行时获取、显示维度 */
  public dims: Dimension[]
  public widgetInstance: WidgetBase
  public showRuntimeAgg = false

  private widgetDataService: WidgetDataService
  /** 元部件的代码元数据，非数据元数据，此数据仅能在此组件内，加载元部件脚本时获取 */
  public curWidgetLoadedData: WidgetLoadedData
  /** 被注入当前元部件实例中的 WidgetInstanceInfo。 */
  public widgetInstanceInfo: WidgetInstanceInfo
  /** scoped 后的 css 的 innerHtml，直接塞模板里就能生效，以节省性能 */
  public scopedCssInnerHtml
  /**
   * 公共配置项 widget comp style，若开启，则根据 pv 中的值计算此值。
   * 若无此值，会
   */
  public wcs = null
  public autoRefreshDataTimer
  /** 此组件内实际使用的 basic widget info, input 属性那个仅作输入属性使用 */
  public innerBasicWidgetInfo: BasicWidgetInfo

  public curbasicWidgetInfo
  public prebasicWidgetInfo
  public curwidgetId
  public prewidgetId
  public isfullscreen = false
  public showfullscreen = true
  /**
   * 记录所有订阅, 组件销毁时全部取消订阅
   */
  private subscriptions: Subscription[] = []

  /**
   * 当前部件框架的筛选参数配置。（相对应的，innerDpValues 是元部件内部的）
   * 此属性结构：{dp, value, fs}[]
   * pv 中保存结构：{fs, dp}[]（筛选控件 + 取数参数 = 筛选）
   * 部件实例中保存 { {fs, dp}: filterValue }
   */
  filterDpFsValueList: DpFsVal[] = []
  /**
   * 用于放大预览时，保存放大前的筛选参数
   */
  curFilterDpFsValueList: DpFsVal[]
  // curDpValues: DpValue[] = []
  /**
   * 联动传来的 dpValues
   */
  linkDpValues: DpValue[] = []
  /**
   * 元部件内部调用取数时，传来的筛选参数-值数据，比如元部件内的分页；
   * 为何要在此存储一份，每次现传不好吗？原因与前面的 curDpValues 一样：
   * 当前部件框架内调用取数时，也要同时传递元部件内的筛选状态，
   * 所以每次部件传来新的 dpValues 都存下来。
   */
  innerDpValues: DpValue[] = []
  jsOnBootCallbacks: {
    onLoadData?: (any) => any
  }

  constructor(
    private loader: DynamicLoaderService,
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private modal: NzModalService,
    private http: HttpClient,
    private translate: TranslateService,
    private widgetEventService: WidgetEventService,
    private insSerivce: InstanceService,
    private widgetRepo: WidgetRepo,
    public sanitizer: DomSanitizer,
    private rtfUtil: RtfUtilService,
    private widgetAnonymityRepo: WidgetAnonymityRepo
  ) {
    this._initWidgetEnv();
    // 当前 rtf 标签页被切换至显示态时, 且未, 触发部件刷新
    // TODO: 浏览器标签页切换是否触发? 加配置项?
    this.rtfUtil.onRtfTabChangeToMe((toTabId, curTabId) => {
      return toTabId === curTabId || (toTabId === 'defaultpage-dashboard' && !curTabId)
    }).subscribe(() => {
      const wor = (<WidgetOnRefresh>this.widgetInstance);
      if (wor.onRefresh) {
        wor.onRefresh();
      }
    })
  }
  /**
   * 全局初始化
   */
  private async _initWidgetEnv() {
    // i18n
    if (!this.translate.defaultLang) this.translate.setDefaultLang('zh-CHS');
    if (!this.translate.currentLang) {
      this.translate.use(curLang)
    }
    if (!window.widgetI18nResLoaded) {
      const { currentLang } = this.translate
      window.widgetI18nResLoaded = true
      const i18nRes = await this.http.get(`${WEB_PATH_HOME}assets/i18n/${currentLang}.json?v=${BUILD_VERSION_PLACEHOLDER}`)
      this.translate.setTranslation(currentLang, i18nRes, true)
    }
  }

  ngOnInit() {
    this.widgetCompCreated.emit(this)
    // if (this.initFilterDpFsValList) this.filterDpFsValueList = this.initFilterDpFsValList.slice()
    this.insSerivce.addInsInfo(this.instanceId, this, 'widgetComp')
    // 监听 resizeTarget 的大小变化，变化时调用元部件.onResized()，传入 DomRectReadonly
    if (this.resizeTarget) {
      new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect
        if (this.widgetInstance) this.widgetInstance.onResized(width, height)
        this.width = width
        this.height = height
        this.cd.markForCheck()
      }).observe(this.resizeTarget.nativeElement)
    }
    // this.renderwidget()
    if (this.isfullscreen == true) {
      this.innerBasicWidgetInfo = this.basicWidgetInfo
      this.renderWidget()
    }

    if (this.propValues) {
      // 如果有'加载前脚本'，运行
      const jsBeforeLoadCode = this.propValues[jsBeforeLoadProp.name]
      if (jsBeforeLoadCode) {
        try {
          this.jsOnBootCallbacks = new Function('args', this.propValues[jsBeforeLoadProp.name])({
            propValues: this.propValues,
            widgetContainer: this,
          })
        } catch (error) {
          Log.Warn(error)
        }
      }
    }
  }

  ngOnChanges(c: SimpleChanges) {
    const { basicWidgetInfo, widgetId, fallbackTitle, isDevMode } = c
    if (isDevMode) {
      this.mode = isDevMode.currentValue ? WidgetContainerMode.DEV : WidgetContainerMode.AOT
    }
    if (basicWidgetInfo
      && basicWidgetInfo.currentValue
      && basicWidgetInfo.currentValue !== basicWidgetInfo.previousValue
      // 这层条件为了以防外面先传了 widgetId, 再传了 basicWidgetInfo 进来，会 render widget 两次。
      && basicWidgetInfo.currentValue !== this.innerBasicWidgetInfo) {
      this.innerBasicWidgetInfo = this.basicWidgetInfo
      this.renderWidget()
    }
    if (widgetId) {
      const { currentValue: cWidgetId, previousValue: pWidgetId } = widgetId
      if (cWidgetId && cWidgetId !== pWidgetId) {
        this.widgetAnonymityRepo.getWidgetById(cWidgetId).subscribe(widgetInfo => {
          this.mode = widgetInfo.isDevMode ? WidgetContainerMode.DEV : WidgetContainerMode.AOT
          this.innerBasicWidgetInfo = widgetInfo as BasicWidgetInfoAotMode
          this.widgetInfoLoaded.emit(widgetInfo)
          this.renderWidget()
        })
      }
    }
    if (fallbackTitle) {
      const { currentValue, previousValue } = fallbackTitle
      if ((currentValue || currentValue === '') && currentValue !== previousValue) {
        this.setTitle()
      }
    }
    // if (c.initFilterDpFsValList && this.propValues.filter && this.insSerivce.getInitialDataLoaded) {
    //   this.tx(this.propValues.filter)
    //   // this.loadNSetData()
    // }
  }
  private objTodelete(arr, objvalue, prop) {
    let index = arr.findIndex(item => item[prop] === objvalue);
    if (index !== -1) {
      arr.splice(index, 1);
    }
  }

  public setTitle() {
    const title = this.propValues.title || this.fallbackTitle
    if (this.widgetInstance) this.widgetInstance.title = title
    if (this.widgetInstanceInfo) this.widgetInstanceInfo.title = title
  }

  allPropertyValueSetPromise: Promise<any>
  // TODO: 这些都改造成，参数为一个对象，里面可以一次塞多个属性-值，全部更新后再触发 onPropertyChange，
  // 否则像初始化完要赋多个值的情况，通知触发太频繁了，也会导致频繁触发元部件渲染、数据加载
  /**
   * 外部更新元部件 propValues 的某属性后，需要调用此方法，否则我们不知道 propValues 发生了变化。
   * 不太好精简掉这个方法。尝试过用 keyValueDiffer 在 ngDoCheck 中检测 propValues 变化，但仅能检测一层。
   */
  public async setPropertyValue(propName: string, pValue, init = false) {
    // TODO: portlet 结构改变后，可以省去这一步：__extra 应该根本不会传进来
    if (propName === '__extra') return
    // 改当组件前存的未处理过的值
    if (!this.propValuesBeforeProcess) this.propValuesBeforeProcess = {}
    this.propValuesBeforeProcess[propName] = pValue
    // 如果不是 object，则判断值如果 pValue 与当前元部件实例中相同，就不传给部件了
    if (typeof pValue !== 'object' && this.widgetInstance[propName] === pValue) return
    // 递归处理 pv
    const value = this.processPV(pValue)

    // ----- 上方可做值处理 -----

    // 改元部件值
    this.widgetInstance[propName] = value
    // 改当组件前存的值
    if (!this.propValues) this.propValues = {}
    this.propValues[propName] = value
    // 当 title 被 set，调用专门的 set title 方法
    if (propName === 'title') {
      this.setTitle()
    }
    this.cd.markForCheck()

    // ----- 下方为当属性变化时的动作，不再对 propValues 发生改变 -----

    // 通知，因为元部件可能需要这个时机
    // 防止没有这个方法的老元部件报错，后面都会升级掉
    if (this.widgetInstance.onPropertyChange) {
      this.widgetInstance.onPropertyChange(propName, value)
    }
    if (init && this.widgetInstance.onPropChangeInit) {
      this.widgetInstance.onPropChangeInit(propName, value)
    }
    if (!init && this.widgetInstance.onPropChange) {
      this.widgetInstance.onPropChange(propName, value)
    }
    if (propName === FILTER_PROP_NAME) {
      // this.filterDpFsValueList.length = 0;//清空数组
      if (this.initFilterDpFsValList) this.filterDpFsValueList = this.initFilterDpFsValList.slice();
      const pFilter = (pValue as FilterConfig[]).filter(fc => fc.dpId);
      const promises = pFilter.map(async fc => {
        // 日期范围，一个 fc 里有俩 dpId
        if (fc.fs.type === 'date' && fc.fs.date.type === 'range') {
          this.objTodelete(this.filterDpFsValueList, fc.dpId, 'dpId');
          this.objTodelete(this.filterDpFsValueList, fc.dpId2, 'dpId');//合并部件参数
          const [date1, date2] = await FilterSchema.getDefaultValue(fc.fs) as unknown as Date[]
          this.filterDpFsValueList.push({
            value: date1,
            dpId: fc.dpId,
            fs: fc.fs,
          }, {
            value: date2,
            dpId: fc.dpId2,
            fs: fc.fs,
          })
        } else { // 普通的单 dpId 情况
          this.objTodelete(this.filterDpFsValueList, fc.dpId, 'dpId');//合并部件参数
          const value = await FilterSchema.getDefaultValue(fc.fs);
          this.filterDpFsValueList.push({
            value: value,
            dpId: fc.dpId,
            fs: fc.fs,
          })
        }
      })
      await Promise.all(promises)
      // TODO:
      // if (pValue && pValue.slice) this.propValues[FILTER_PROP_NAME] = pValue.slice()
      // this.cd.markForCheck()
    }
    if (propName === COMMON_STYLE_PROP_NAME) {
      const cssStr: string = this.propValues[COMMON_STYLE_PROP_NAME]
      if ((!cssStr && cssStr !== '') || !cssStr.replace) return
      const selectorsRegex = /([^\r\n,{}]+)(,(?=[^}]*{)|\s*{)/g
      const css = cssStr.replace(selectorsRegex, `#w${this.instanceId} $&`)
      this.scopedCssInnerHtml = this.sanitizer.bypassSecurityTrustHtml('<style>' + css + '</style>')
    }
    // wcs
    const wcsEnabled = propName === 'wcsEnabled' ? pValue : this.propValues['wcsEnabled']
    if (propName === 'wcsEnabled') {
      if (wcsEnabled) {
        this._setWcs()
      } else {
        this._clearWcs()
      }
    } else if (propName.startsWith('wcs') && wcsEnabled) {
      this._setWcs()
    }

  }

  private _setWcs() {
    const { wcsBorderRadius, wcsBgEnabled, wcsBgColor, wcsEnableShadow, wcsPadding } = this.propValues
    this.wcs = {
      background: wcsBgEnabled && wcsBgColor
        ? wcsBgColor.includes(',')
          ? 'linear-gradient(45deg, ' + wcsBgColor + ')'
          : wcsBgColor
        : 'none',
      'padding.px': wcsPadding,
      'box-shadow': wcsEnableShadow ? '0 2px 10px 0 rgba(81, 87, 111, .12)' : 'none',
      'border-radius.px': wcsBorderRadius,
    }
  }
  private _clearWcs() {
    this.wcs = null
  }

  /* 递归检查 pv，做特殊处理，比如国际化、帮助 */
  private processPV(value) {
    const { currentLang } = this.translate
    try {
      value = JSON.parse(JSON.stringify(value))
    } catch (e) { }
    return _processPVRecursively(value)

    function _processPVRecursively(pValue) {
      if (Array.isArray(pValue)) {
        pValue.forEach((v, i) => {
          pValue[i] = _processPVRecursively(v)
        })
        return pValue
        // i18n: 如果 value 中包含 en 或 zh-CHS 或 当前语言的key，则是 i18n 字段
      } else if (pValue && !pValue._notProcess && typeof (pValue.en + pValue['zh-CHS'] + pValue[currentLang]) === 'string') {
        return pValue[currentLang]
        // help
      } else if (pValue && pValue.__type === 'help') {
        return pValue.value
      } else if (pValue && ['Object'].includes(pValue.constructor.name)) {
        Object.keys(pValue).forEach(key => {
          pValue[key] = _processPVRecursively(pValue[key])
        })
        return pValue
      } else {
        return pValue
      }
    }
  }
  public async renderWidget() {
    // 加载渲染这个 widget 需要的一切元数据
    const { baseUrl } = this.innerBasicWidgetInfo
    this.curWidgetLoadedData = await this.loader.loadWidget(this.innerBasicWidgetInfo, this.propValues, this.mode)
    // TODO: propValues 是不是不用经过 loader，直接调 widgetPropService 获取就行？
    const { widgetCompFactory, widgetModuleFactory, propValues, widgetConfig } = this.curWidgetLoadedData
    this.propValues = propValues
    this.curWidgetConfig = widgetConfig
    this.initRuntimeAgg()
    if (this.curWidgetConfig.enableLoadingI18n) this.loadI18nRes()
    // 元部件加载完成事件
    this.widgetLoaded.emit(this.curWidgetLoadedData)
    // 加载完成 promise，用于外部正确地获取加载完成后状态时机
    if (this.resolveWidgetLoadedPromise) this.resolveWidgetLoadedPromise()
    // 删老元部件
    if (this.widgetContainer) this.widgetContainer.remove()
    this.widgetInstanceInfo = {
      id: this.instanceId,
      assetsBaseUrl: this.assetsBaseUrl || this.innerBasicWidgetInfo.baseUrl + '/assets/',
      // title: this.fallbackTitle,
      ...this.widgetInstanceInfoMixin,
      widgetContainer: this,
    }
    
    // 创建注入器
    const injector = Injector.create({
      providers: [{
        provide: WidgetInstanceInfo, /** 元部件实例信息 */
        useValue: this.widgetInstanceInfo
      }, {
        provide: WidgetDataService, /** 元部件数据服务 */
        useFactory: () => {
          this.widgetDataService = new WidgetDataService()
          const widgetLoadDataSubscription = this.widgetDataService.onLoadData
            .pipe(delay(0)) // 为避免用 of(data) 会同步返回数据，导致 data service 中的 onGetData 还没返回到元部件手里就触发完了
            .subscribe(options => {
              if (options && options.dpValues) {
                this.innerDpValues = options.dpValues
              }
              this.loadNSetData()
              // 若已配置，自动刷新数据
              const { autoRefreshData, refreshInterval, refreshWhenTabShow } = this.widgetInstance as any
              if (refreshWhenTabShow) {
                const tabChangeToMeSubscription = this.rtfUtil.onRtfTabChangeToMeForDashboard().subscribe(() => {
                  this.loadNSetData()
                })
                this.subscriptions.push(tabChangeToMeSubscription)
              }
              if (autoRefreshData && refreshInterval) {
                this.autoRefreshDataTimer = setInterval(() => {
                  // 如果已经关掉了，删除此计时器
                  if (!this.widgetInstance['autoRefreshData']) {
                    clearInterval(this.autoRefreshDataTimer)
                    return
                  }
                  this.loadNSetData()
                }, refreshInterval * 1000)
              }
            })
          this.subscriptions.push(widgetLoadDataSubscription)
          return this.widgetDataService
        },
        deps: []
      }],
      parent: this.injector
    })
    // 创建、挂载 widget 组件
    this.widgetInstance = this.widgetContainer.createComponent(
      widgetCompFactory, 0, injector, undefined,
      widgetModuleFactory && widgetModuleFactory.create(this.injector)
    ).instance as WidgetBase

    // 如果有配置脚本，运行
    const jsOnBootCode = this.propValues[jsOnBootProp.name]
    if (jsOnBootCode) {
      try {
        this.jsOnBootCallbacks = new Function('args', this.propValues[jsOnBootProp.name])({
          propValues,
          widgetConfig,
          widgetInstance: this.widgetInstance,
          widgetContainer: this,
        })
      } catch (error) {
        Log.Warn(error)
      }
    }
    // 设置 widget 的 properties 的 values
    // TODO: 改为一次配多个
    // Object.keys(propValues).forEach(prop => {
    //   this.setPropertyValue(prop, propValues[prop], true)
    // })
    this.allPropertyValueSetPromise = Promise.all(
      Object.keys(propValues)
        .map(prop => this.setPropertyValue(prop, propValues[prop], true))
    )

    this.setTitle()
    // this.widgetInstance.title = this.fallbackTitle
    this.widgetInstance.id = this.instanceId
    // this.widgetInstance.portletId = this.portlet.id
    this.widgetInstance.assetsBaseUrl = this.assetsBaseUrl || baseUrl + '/assets/'
    if (this.widgetInstance.useDataEngine) this.loadNSetData()

    // 当前 rtf 标签页被切换至显示态时, 且未, 触发部件刷新
    // TODO: 浏览器标签页切换是否触发? 加配置项?
    const widgetInstanceWOR = (<WidgetOnRefresh>this.widgetInstance);
    if (widgetInstanceWOR.onRefresh) {
      const tabChangeToMeSubscription = this.rtfUtil.onRtfTabChangeToMeForDashboard().subscribe(() => {
        widgetInstanceWOR.onRefresh()
      })
      this.subscriptions.push(tabChangeToMeSubscription)
    }

    // event
    this.initWidgetEventListener()
    // 元部件初始化完成事件
    this.widgetInit.emit(this.curWidgetLoadedData)
    // this.isLoading = false
    this.status = WCStatus.Normal
    // this.cd.markForCheck()
    this.cd.detectChanges()
    // 一个部件卡片初次渲染，它的元部件示例出现之前，
    // resize observer 就已经检测到大小、试图调用 onResized 了，
    // 但因为没有示例而调不了，导致元部件一直收不到大小，除非之后再次改变。
    // 所以在此显式调用。
    this.widgetInstance.onResized(this.width, this.height)
  }

  private loadI18nRes() {
    const { currentLang } = this.translate
    const { name, baseUrl } = this.innerBasicWidgetInfo
    const { customI18nResourceUrl } = this.curWidgetConfig
    const i18nResUrl = customI18nResourceUrl || `${baseUrl}/assets/i18n/${currentLang}.json?v=${new Date().getTime()}`

    // 缓存曾加载过的资源文件名，下次相同文件不再请求。缓存资源文件名，是因为可能多个元部件指定相同的资源文件。
    if (loadedI18nUrls.includes(i18nResUrl)) return
    loadedI18nUrls.push(i18nResUrl)

    return this.http.get(i18nResUrl).toPromise().then(res => {
      this.translate.setTranslation(currentLang, res, true)
    }, () => {
      Log.Warn(`元部件 ${name} 无 ${currentLang} 的资源文件`)
    }).finally(() => {
      return Promise.resolve()
    })
  }
  /**
   * 点击刷新按钮
   * 1. 若元部件实现了 onRefresh 回调，则调它，适用部件：需要自身特殊实现刷新
   * 2. 若未实现 onRefresh，则部件实例重新初始化
   * 说明：2 原先是调用数据集取数 loadNSetData，但有两个问题所以改掉了：
   *   2-1. 即使报错，也不提示报错。因为它的理想实现应该是，若有数据集，才调数据集取数，否则不需要配数据集的元部件一取数会报错。
   *   但因为无法区分当前元部件是否配置了数据集，所以只好都不报错了。
   *   若刷新只是 loadNSetData，则不需要配数据集的部件会报错，但如果都注册 onRefresh 来解决，
   *   要改的元部件就太多了，而且还要单独实现
   *   2-2. 非数据集取数的部件无法刷新
   */
  public handleClickRefresh() {
    const wor = (<WidgetOnRefresh>this.widgetInstance);
    if (wor.onRefresh) {
      wor.onRefresh()
    } else {
      this.renderWidget()
    }
  }
  /**
   * 塞数据入口 1/2: 加载数据并塞给部件
   * TODO: loadNSetData 可能是不带参数的。取数参数可能存在当前部件框架实例里，直接取。
   * loadNSetData 有没有可能带着来自部件的额外参数？
   * showErrorIfError: 若接口报错，是否显示报错，默认显示
   */
  public async loadNSetData(
    // param: {
    //   dpValues?: DpValue[],
    // } = {},
    showErrorStatusIfError = true
  ) {
    const ob = this.getData({
      dpValues: await this.getCurDpValues(),
      runtimeAgg: this.runtimeAgg
    })
    // 未配置时则为空
    if (!ob) return
    this.status = WCStatus.LoadingData
    ob.subscribe(async dataWithDims => {
      if (this.jsOnBootCallbacks && this.jsOnBootCallbacks.onLoadData) {
        dataWithDims = await this.jsOnBootCallbacks.onLoadData(dataWithDims)
      }
      this.setData(dataWithDims)
    }, showErrorStatusIfError && this.handleDataError.bind(this))
  }
  /**
   * 获取当前，合并、处理过后，可以直接传给接口的 dpValues
   */
  public async getCurDpValues() {
    await this.allPropertyValueSetPromise
    if (!this.filterDpFsValueList) return []
    // cur dp values 是通过 dp fs value 计算得到的，因为 fs 中的 formatter 会影响 value；
    // link dpValues、inner dpValues 不需要，因为联动、元部件里面根本没有 fs，即使有 formatter 也写在元部件里。
    if (this.isfullscreen && this.curFilterDpFsValueList) {
      const filterDpValues = this.curFilterDpFsValueList.map(dpFsValue => ({
        dpId: dpFsValue.dpId,
        value: FilterSchema.format(dpFsValue.fs, dpFsValue.value)
      } as DpValue))
      return mergeDpValuesForNew(
        filterDpValues,
        this.linkDpValues,
        this.innerDpValues
      )
    }
    else {
      const filterDpValues = this.filterDpFsValueList.map(dpFsValue => ({
        dpId: dpFsValue.dpId,
        value: FilterSchema.format(dpFsValue.fs, dpFsValue.value)
      } as DpValue))
      return mergeDpValuesForNew(
        filterDpValues,
        this.linkDpValues,
        this.innerDpValues
      )
    }
  }

  // TODO: 将来可能区分联动、其他类型，像 dpValues 一样
  feFilterConfigs: FeFilterConfig[] = []
  public setFeFilterConfig(feFilterConfig: FeFilterConfig) {
    // TODO: 支持多个合并
    this.feFilterConfigs = [feFilterConfig]
    this.loadNSetData()
  }
  /** 前端过滤，现已用于 feFilter 类型的联动 */
  private feFilter(tableData: any[]) {
    return tableData.filter(rowData => {
      return !this.feFilterConfigs
        .map(c => rowData[c.fieldName] === c.value)
        .includes(false)
    })
  }
  /**
   * 塞数据入口 2/2
   * 给元部件重新设置数据结果，外部可调用此方法给部件设置数据。
   */
  public async setData(pDataWithDims: DataWithDims) {
    let dataWithDims = this.jsOnBootCallbacks && this.jsOnBootCallbacks.onLoadData
      ? await this.jsOnBootCallbacks.onLoadData(pDataWithDims)
      : pDataWithDims
    // 如果外面没有给 DataWithDims 而是简单数据列表，就简单转一把。以兼容 config 里的假数据等。
    if (!pDataWithDims.dimensions && Array.isArray(pDataWithDims)) {
      dataWithDims = {
        dimensions: pDataWithDims[0]
          ? Object.keys(pDataWithDims[0]).map(key => ({ name: key, label: key }))
          : [],
        tableData: pDataWithDims
      }
    }
    // 前端过滤
    dataWithDims.tableData = this.feFilter(dataWithDims.tableData)
    this.data = dataWithDims.tableData
    this.dims = dataWithDims.dimensions
    // 给元部件
    if (this.widgetDataService) this.widgetDataService.setData(dataWithDims)
    if (this.widgetInstance && this.widgetInstance.useDataEngine) this.widgetInstance.onGetData(dataWithDims.tableData) // 兼容用途
    // 给元部件设置完数据，若有运行时聚合，还需要重新绑定字段
    this.setRuntimeAggDims()
    // 更新部件状态
    if (
      !this.curWidgetConfig.showIfNoData
      && (
        !dataWithDims
        || !dataWithDims.tableData
        || dataWithDims.tableData.length === 0
        || this.isTableDataNull(dataWithDims)
      )
    ) {
      this.status = WCStatus.DataEmpty
    } else {
      this.status = WCStatus.Normal
    }
    this.cd.markForCheck()
  }

  isTableDataNull(dataWithDims: DataWithDims) {
    var isNull = false
    if (dataWithDims.tableData.length === 1) {
      isNull = true
      for (let dim of dataWithDims.dimensions) {
        if (dataWithDims.tableData[0][dim.name] !== null) {
          isNull = false
        }
      }
    }
    return isNull
  }
  /**
   * 给外部：data param 型联动此部件时，调用此方法
   * @param dpValues
   */
  public setLinkDpValues(dpValues: DpValue[]) {
    mergeDpValues(this.linkDpValues, dpValues)
    this.loadNSetData()
  }
  public cleanAllLink() {
    this.feFilterConfigs = []
    this.linkDpValues = []
    this.loadNSetData()
  }
  /**
   * 给外部使用：外部通过此方法设置某个部件启动器的筛选条件，用于页面级筛选等
   * 会覆盖掉原先的同名 dpValues
   * set 完成后会立即调用加载、显示数据
   * @param dpFsValues
   */
  public setFilterDpValues(dpFsValues: DpFsVal[], triggerLoadNSetData = true) {
    mergeDpValues(this.filterDpFsValueList, dpFsValues)
    this.curFilterDpFsValueList = this.filterDpFsValueList
    if (triggerLoadNSetData) this.loadNSetData()
  }

  handleDataError = () => {
    // 若当前元部件非 “不配数据集也展示”，才显示 “数据加载失败”
    if (!this.curWidgetConfig.showIfNoData) {
      this.status = WCStatus.DataError
    } else {
      this.status = WCStatus.Normal
    }
    this.cd.markForCheck()
  }
  handleClickStatusMore() {
    this.modal.error({
      nzTitle: this.translate.instant('widgetError'),
      nzContent: this.translate.instant('configureInvalid')
    })
  }
  filterValues: any[]
  handleFilterChange(dpValues: DpValue[]) {
    mergeDpValues(this.filterDpFsValueList, dpValues)
    this.curFilterDpFsValueList = this.filterDpFsValueList
    this.loadNSetData()
  }
  // @Input() initRuntimeAgg: RuntimeAggOption
  /**
   * 此 runtimeAgg 存储当前运行时用户选的、最新的 runtimeAgg。
   * extra 中的是存储在部件中的。
   * widget 不负责把 runtimeAgg 放到 extra 里，因为这里不确定是否需要持久化。
   */
  runtimeAgg: RuntimeAggOption
  SHOW_RUNTIME_AGG_PROP_NAME = SHOW_RUNTIME_AGG_PROP_NAME
  handleRuntimeAggChange(runtimeAgg: RuntimeAggOption) {
    // if (objectLiteralEqual(runtimeAgg, this.runtimeAgg)) return
    this.runtimeAgg = runtimeAgg
    // 下面两行顺序严格，因为外部加载数据时可能要依赖当前最新的 runtimeAgg
    this.runtimeAggChange.emit(runtimeAgg)
    this.loadNSetData()
  }
  /** 当前写法需要保证 renderWidget() 已完成 */
  handleTitleBarInit(titleBar: TitleBarComponent) {
    if (this.showRuntimeAgg) {
      titleBar.setRuntimeAgg(this.runtimeAgg)
    }
  }
  initRuntimeAgg() {
    // if (this.showRuntimeAgg) {
    this.runtimeAgg = (this.propValues && this.propValues.__extra) ? this.propValues.__extra.runtimeAgg : null
    // }
  }
  // TODO: 以后把 su 放 basic... 里
  /**
   * 初始化部件事件监听
   */
  private initWidgetEventListener() {
    if (!this.linkMap) return
    if (this.amIFilterWidget()) {
      this.initOneEventListener(FILTERS_WIDGET_PROP_NAME)
    } else {
      this.widgetEventService
        .getEventsByWidgetName(this.innerBasicWidgetInfo.name, this.propValues)
        .forEach(eventConfig => {
          this.initOneEventListener(eventConfig.propName)
        })
    }
  }
  /**
   * eventPropName 是事件对应的实际属性名，
   * eventKey 配置在 link 里的事件名。
   * 一般他俩是一样的，后者可以不传。
   * 若是筛选部件，则两者不同，一个是 'filter'，一个是实际的事件（筛选）名称。
   */
  private initOneEventListener(eventPropName: string) {
    const eventEmitter = this.widgetInstance[eventPropName]
    if (eventEmitter) eventEmitter.subscribe(widgetEventParam => {
      const linkEventKey = this.amIFilterWidget()
        ? (widgetEventParam as FilterWidgetEventParam).eventKey
        : eventPropName
      // 根据 link map 调用其他 ins 根据 重新加载数据
      const curEventLinkConfig = this.linkMap[linkEventKey]
      Object.keys(curEventLinkConfig.targets).forEach(pInsId => {
        const targetPIns = curEventLinkConfig.targets[pInsId]
        if (
          !targetPIns
          || !targetPIns.active
          // || !curEventLinkConfig.targets[pInsId].dpId
        ) return
        const { fieldName } = curEventLinkConfig
        const { active, dpId, type, fieldName: rowFilterFieldName } = targetPIns
        const targetWComp = this.insSerivce.getInsByPInsId(pInsId).widgetComp
        if (targetWComp) {
          if (type === 'dataParam') {
            targetWComp.setLinkDpValues([{
              dpId: curEventLinkConfig.targets[pInsId].dpId,
              // TODO: 联动参数值的处理加在这
              value: String(widgetEventParam[fieldName]),
            }])
          } else if (type === 'feFilter') {
            targetWComp.setFeFilterConfig({
              fieldName: rowFilterFieldName,
              value: widgetEventParam[fieldName],
            })
          }
        }
      })
    })
  }
  setRuntimeAggDims() {
    // 无完整配置就别走下面了
    if (!this.runtimeAgg
      || !this.runtimeAgg.dimensions || !this.runtimeAgg.dimensions[0]
      || !this.runtimeAgg.indicators || !this.runtimeAgg.indicators[0]
    ) return
    chartAggFieldMap[this.innerBasicWidgetInfo.name].forEach(aggField => {
      this.setPropertyValue(aggField.name, aggField.valueFactory(this.runtimeAgg, this.dims))
    })
  }
  // handleExportPdf() {
  //   exportPdf(this.resizeTarget.nativeElement, this.widgetInstance.title)
  // }
  handleExportExcel() {
    exportExcel(this.data, this.widgetInstance.title)
  }
  /**
   * @deprecated
   * 没有 filter widget 了
   */
  amIFilterWidget() {
    return this.innerBasicWidgetInfo.name === FILTERS_WIDGET_NAME
      && this.innerBasicWidgetInfo.baseUrl.includes('/epp/')
  }

  handlefullscreenPreview() {
    this.modal.create({
      nzContent: WidgetComponent,
      nzBodyStyle: {
        height: '80vh',
      },
      nzComponentParams: {
        instanceId: this.instanceId,
        basicWidgetInfo: this.basicWidgetInfo,
        propValues: this.propValues,
        fallbackTitle: this.fallbackTitle,
        getData: this.getData,
        scopedCssInnerHtml: this.scopedCssInnerHtml,
        showfullscreen: false,
        isfullscreen: true,
        /** 获取放大前的筛选参数 */
        curFilterDpFsValueList: this.curFilterDpFsValueList,
      },
      nzTitle: this.translate.instant('EnlargePreview'),
      nzWidth: "90vw",
      nzFooter: null,
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }
}
